/**
 * InscricoesConfigModal — preço + templates de email por idioma (PT/EN/ES).
 *
 * Cada idioma tem TRÊS emails editáveis:
 *   1. "credentials" — enviado quando a inscrição é feita (ou reenviada)
 *   2. "paid"        — enviado quando o admin marca a inscrição como PAGA
 *   3. "unpaid"      — enviado quando o admin reverte para NÃO PAGA
 *
 * O corpo do email + métodos de pagamento usam o WYSIWYG (sem HTML).
 * Assunto e remetente são inputs de texto simples.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save, RotateCcw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import PaymentMethodPills from "@/pages/components/PaymentMethodPills";

const LANGS = [
  { code: "pt", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

const EMAIL_KINDS = [
  {
    id: "credentials",
    label: "Envio das credenciais",
    desc: "Enviado logo após a inscrição ou ao clicar em Reenviar",
    subjectField: "subject",
    bodyField: "body",
    showPaymentMethods: true,
    placeholders: ["stk_id", "school", "count", "price", "total", "credentials_table", "pay_buttons", "payment_methods"],
  },
  {
    id: "paid",
    label: "Confirmação de pagamento",
    desc: "Enviado quando a inscrição é marcada como PAGA",
    subjectField: "paid_subject",
    bodyField: "paid_body",
    showPaymentMethods: false,
    placeholders: ["stk_id", "school", "count", "total", "paid_at"],
  },
  {
    id: "unpaid",
    label: "Reversão para não paga",
    desc: "Enviado quando a inscrição volta a NÃO PAGA",
    subjectField: "unpaid_subject",
    bodyField: "unpaid_body",
    showPaymentMethods: true,
    placeholders: ["stk_id", "school", "total", "pay_buttons", "payment_methods"],
  },
];

const PLACEHOLDER_LABELS = {
  stk_id: "ID da inscrição (STK-42)",
  school: "Nome da escola",
  count: "Nº de credenciais",
  price: "Preço por credencial",
  total: "Total a pagar",
  credentials_table: "Tabela de credenciais",
  pay_buttons: "Botões coloridos de pagamento",
  payment_methods: "Métodos de pagamento (texto livre)",
  paid_at: "Data do pagamento",
};

export default function InscricoesConfigModal({ onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState("pt");
  const [activeKind, setActiveKind] = useState("credentials");
  const [price, setPrice] = useState(3.5);
  const [currency, setCurrency] = useState("EUR");
  const [templates, setTemplates] = useState({});
  // Live preview of the pay-buttons block that gets embedded via the
  // `{{pay_buttons}}` placeholder — shows the admin EXACTLY what schools
  // will see in the email for the currently-enabled payment methods.
  const [pmMethods, setPmMethods] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [cfg, pm] = await Promise.all([
          api.get("/inscriptions/config"),
          // Admin endpoint returns the raw settings (with `enabled` toggles)
          // — same source of truth the /pagar/{token} public page uses.
          api.get("/admin/payment-settings").catch(() => ({ data: {} })),
        ]);
        const data = cfg.data;
        setPrice(Number(data?.price_per_credential ?? 3.5));
        setCurrency(data?.currency || "EUR");
        setTemplates(data?.templates || {});
        // Reshape admin config into the { methods.X.enabled } contract the
        // <PaymentMethodPills/> component expects.
        const pmData = pm.data || {};
        setPmMethods({
          stripe:     { enabled: !!pmData?.stripe?.enabled },
          paypal:     { enabled: !!pmData?.paypal?.enabled },
          ifthenpay:  { enabled: !!pmData?.ifthenpay?.enabled },
          bank_transfer: { enabled: !!pmData?.bank_transfer?.enabled },
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro a carregar config");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tpl = templates[activeLang] || {};
  const setTplField = (field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [activeLang]: { ...(prev[activeLang] || {}), [field]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/inscriptions/config", {
        id: "inscription_config",
        price_per_credential: Number(price) || 0,
        currency,
        templates,
      });
      toast.success("Configuração guardada");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  const kind = EMAIL_KINDS.find((k) => k.id === activeKind) || EMAIL_KINDS[0];
  const kindPlaceholders = kind.placeholders.map((k) => ({
    key: k,
    label: PLACEHOLDER_LABELS[k] || k,
  }));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="inscricoes-config-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-[Fredoka] font-bold text-slate-900">Preço & Emails das Inscrições</h2>
            <p className="text-xs text-slate-500 mt-0.5">Aplica-se a novos emails e a reenvios em cada idioma</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100" data-testid="btn-close-config">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">A carregar…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Price */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-2">
                <Label>Preço por credencial</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  data-testid="cfg-price"
                />
                <p className="text-xs text-slate-500 mt-1">Total da inscrição = preço × nº de credenciais.</p>
              </div>
              <div>
                <Label>Moeda</Label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="cfg-currency">
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>

            {/* Language tabs */}
            <div className="border-b border-slate-200 flex gap-1">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setActiveLang(l.code)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                    activeLang === l.code
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`} data-testid={`tab-lang-${l.code}`}>
                  {l.label}
                </button>
              ))}
            </div>

            {/* Global sender for this language */}
            <div>
              <Label>Remetente global (opcional para {LANGS.find((l) => l.code === activeLang)?.label})</Label>
              <Input value={tpl.sender || ""} onChange={(e) => setTplField("sender", e.target.value)}
                placeholder="ex: no-reply@supertmatik.com" data-testid={`cfg-sender-${activeLang}`} />
              <p className="text-xs text-slate-500 mt-1">Deixa vazio para usar o remetente global do SMTP/SendGrid. Aplica-se aos 3 emails deste idioma.</p>
            </div>

            {/* Email kind sub-tabs */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-1 flex gap-1">
              {EMAIL_KINDS.map((k) => (
                <button key={k.id} onClick={() => setActiveKind(k.id)}
                  className={`flex-1 px-3 py-2 rounded-md text-xs font-semibold transition-colors text-center ${
                    activeKind === k.id ? "bg-white shadow text-blue-700" : "text-slate-500 hover:text-slate-800"
                  }`} data-testid={`tab-kind-${k.id}`}>
                  {k.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 -mt-2">{kind.desc}</p>

            {/* Subject */}
            <div>
              <Label>Assunto</Label>
              <Input value={tpl[kind.subjectField] || ""} onChange={(e) => setTplField(kind.subjectField, e.target.value)}
                placeholder={`Ex: SUPERTMATIK — ${kind.label}`}
                data-testid={`cfg-${activeLang}-${kind.id}-subject`} />
            </div>

            {/* Body — rich editor */}
            <div>
              <Label>Corpo do email</Label>
              <SimpleRichTextEditor
                key={`${activeLang}-${kind.id}`}
                value={tpl[kind.bodyField] || ""}
                onChange={(html) => setTplField(kind.bodyField, html)}
                placeholders={kindPlaceholders}
                testId={`cfg-${activeLang}-${kind.id}-body`}
                minHeight={260}
              />
            </div>

            {/* Live preview of the {{pay_buttons}} block — only for the two
                email kinds that actually inject it into the body. */}
            {kind.showPaymentMethods && (
              <div className="border border-slate-200 rounded-xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-slate-700 mb-2">
                  <Eye className="w-4 h-4" />
                  <div className="text-sm font-bold">Pré-visualização dos botões de pagamento</div>
                  <span className="text-[11px] text-slate-500">— o que a escola verá no email quando usares <code className="bg-white px-1 rounded">{"{{pay_buttons}}"}</code></span>
                </div>
                <PaymentMethodPills
                  lang={activeLang}
                  methods={pmMethods}
                  onSelect={() => toast("Pré-visualização — o clique real acontece no email/pagina de pagamento")}
                  showPayLater
                />
                <p className="text-[11px] text-slate-500 mt-2">
                  Só aparecem métodos com o toggle activado em <a href="/admin/pagamentos" className="underline">Pagamentos</a>.
                  Em <b>PT</b> podes ter até 5 (Cartão, PayPal, MB WAY, Multibanco, Pagar mais tarde);
                  em <b>EN/ES</b> mostra 3 (MB WAY e Multibanco são só PT).
                </p>
              </div>
            )}

            {/* Payment methods — only for credentials + unpaid */}
            {kind.showPaymentMethods && (
              <div>
                <Label>Métodos de pagamento</Label>
                <SimpleRichTextEditor
                  key={`${activeLang}-pm`}
                  value={tpl.payment_methods || ""}
                  onChange={(html) => setTplField("payment_methods", html)}
                  placeholders={[
                    { key: "stk_id", label: "ID inscrição" },
                    { key: "total", label: "Total" },
                    { key: "school", label: "Escola" },
                  ]}
                  testId={`cfg-${activeLang}-payment-methods`}
                  minHeight={140}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Bloco de <b>texto livre</b> disponível como <code>{"{{payment_methods}}"}</code> — útil para instruções adicionais (IBAN, notas). É partilhado entre os emails deste idioma. Se só quiseres os botões coloridos, remove <code>{"{{payment_methods}}"}</code> do corpo do email.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="btn-cancel-config">
            <RotateCcw className="w-4 h-4 mr-1" /> Cancelar
          </Button>
          <Button onClick={save} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-config">
            <Save className="w-4 h-4 mr-1" /> {saving ? "A guardar…" : "Guardar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
